import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ObservationRequest,
  ObservationRequestForEvaluator,
  ObservationRequestForRequester,
  ObservationPendingListItem,
  EvidenceDraft,
  CriterionResult,
} from '@/types';

const KEYS = {
  byEnrollmentLesson: (enrollmentId: number, lessonId: number) =>
    ['practical-observations', 'by-enrollment-lesson', enrollmentId, lessonId] as const,
  pending: (page: number) => ['practical-observations', 'pending', page] as const,
  pendingCount: ['practical-observations', 'pending-count'] as const,
  detail: (id: number) => ['practical-observations', 'detail', id] as const,
};

export function useObservationRequest(enrollmentId: number, lessonId: number, enabled = true) {
  return useQuery({
    queryKey: KEYS.byEnrollmentLesson(enrollmentId, lessonId),
    queryFn: () =>
      api.get<ObservationRequestForRequester | null>(
        '/practical-observations/by-enrollment-lesson',
        {
          enrollment_id: enrollmentId,
          lesson_id: lessonId,
        },
      ),
    enabled: enabled && enrollmentId > 0 && lessonId > 0,
  });
}

export function useCreateObservationRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { enrollmentId: number; lessonId: number }) =>
      api.post<ObservationRequest>('/practical-observations', {
        enrollment_id: vars.enrollmentId,
        lesson_id: vars.lessonId,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: KEYS.byEnrollmentLesson(vars.enrollmentId, vars.lessonId),
      });
      qc.invalidateQueries({ queryKey: ['practical-observations'] });
    },
  });
}

export function useCancelObservationRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; reason?: string }) =>
      api.patch<{ cancelled: boolean }>(`/practical-observations/${vars.id}/cancel`, {
        reason: vars.reason,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['practical-observations'] }),
  });
}

export function useObservationDetail(id: number) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<ObservationRequestForEvaluator>(`/practical-observations/${id}`),
    enabled: id > 0,
  });
}

export function useObservationPending(page: number, perPage = 20) {
  return useQuery({
    queryKey: KEYS.pending(page),
    queryFn: () =>
      api.get<ObservationPendingListItem[]>('/practical-observations/pending', {
        page,
        per_page: perPage,
      }),
  });
}

export function useObservationPendingCount() {
  return useQuery({
    queryKey: KEYS.pendingCount,
    queryFn: () =>
      api.get<ObservationPendingListItem[]>('/practical-observations/pending', {
        page: 1,
        per_page: 1,
      }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useUploadEvidenceUrl() {
  return useMutation({
    mutationFn: (vars: { requestId: number; file_name: string; content_type: string }) =>
      api.post<{ upload_url: string; key: string }>(
        `/practical-observations/${vars.requestId}/upload-url`,
        { file_name: vars.file_name, content_type: vars.content_type },
      ),
  });
}

export function useCompleteObservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      evaluator_notes?: string;
      criteria_results: Array<
        Pick<CriterionResult, 'criterion_id'> & {
          passed: boolean;
          notes?: string;
        }
      >;
      evidence_keys: EvidenceDraft[];
    }) =>
      api.patch<{ completed: boolean }>(`/practical-observations/${vars.id}/complete`, {
        evaluator_notes: vars.evaluator_notes,
        criteria_results: vars.criteria_results,
        evidence_keys: vars.evidence_keys,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['practical-observations'] }),
  });
}
